import React, { PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppSelector } from './redux-hooks'
import { connectNotificationBridge, getLeadNotificationPath, InboxMessage, InboxSnapshot,
  LeadDestination, normalizeInbox, notificationsConfigured, requestNotifications } from 'utils/cleverTap'
import { isCurrentLoginSession } from 'utils/loginSession'

const syncOsBadge = (count: number) => {
  const nav = navigator as Navigator & { setAppBadge?: (n: number) => Promise<void>; clearAppBadge?: () => Promise<void> }
  const task = count > 0 ? nav.setAppBadge?.(count) : nav.clearAppBadge?.()
  task?.catch(() => undefined)
}

type NotificationContextType = {
  messages: InboxMessage[]
  unreadCount: number
  totalCount: number
  available: boolean
  loading: boolean
  error: string
  refreshUnreadCount: () => Promise<void>
  openNotification: (id: string) => Promise<void>
}

export const NotificationContext = React.createContext<NotificationContextType>({
  messages: [], unreadCount: 0, totalCount: 0, available: false, loading: false, error: '',
  refreshUnreadCount: async () => undefined, openNotification: async () => undefined,
})

const NotificationProvider: React.FC<PropsWithChildren<object>> = ({ children }) => {
  const { isLoggedIn, tokenHeader } = useAppSelector((state) => state.auth)
  const employeeId = isLoggedIn && isCurrentLoginSession(tokenHeader) ? tokenHeader?.employeeid || null : null
  const name = tokenHeader?.ownername
  const email = tokenHeader?.email
  const currentEmployee = useRef(employeeId)
  currentEmployee.current = employeeId
  const [inbox, setInbox] = useState<InboxSnapshot>(() => normalizeInbox(null, null))
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const navigate = useNavigate()
  const navigateRef = useRef(navigate)
  navigateRef.current = navigate

  const acceptInbox = useCallback((data: InboxSnapshot) => {
    if (data?.employeeId !== currentEmployee.current) return
    setInbox(normalizeInbox(data, currentEmployee.current))
  }, [])

  useEffect(() => {
    let connected = true
    const openPush = async () => {
      try {
        const destination = await requestNotifications<LeadDestination | null>('getPendingDeepLink')
        if (connected && destination) navigateRef.current(getLeadNotificationPath(destination))
      } catch (err) { if (connected) setError(err.message) }
    }
    const disconnect = connectNotificationBridge((event, data) => {
      if (event === 'inboxUpdated') acceptInbox(data)
      if (event === 'deepLinkAvailable') void openPush()
    })
    if (window.CleverTapNative) void openPush()
    return () => { connected = false; disconnect() }
  }, [acceptInbox])

  useEffect(() => {
    let active = true
    setInbox(normalizeInbox(null, employeeId))
    syncOsBadge(0)
    setError('')
    if (!notificationsConfigured()) return
    setLoading(true)
    requestNotifications<InboxSnapshot>(employeeId ? 'identify' : 'logout', employeeId ? { employeeId, name, email } : {})
      .then((data) => { if (active) acceptInbox(data) })
      .catch((err) => { if (active) setError(err.message) })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [employeeId, name, email, acceptInbox])

  const refreshUnreadCount = useCallback(async () => {
    if (!employeeId || !notificationsConfigured()) return
    const identity = employeeId
    setLoading(true)
    try {
      const data = await requestNotifications<InboxSnapshot>('refreshInbox')
      if (currentEmployee.current === identity) { acceptInbox(data); setError('') }
    } catch (err) { if (currentEmployee.current === identity) setError(err.message) }
    finally { if (currentEmployee.current === identity) setLoading(false) }
  }, [employeeId, acceptInbox])

  useEffect(() => {
    const refresh = () => { if (!document.hidden) void refreshUnreadCount() }
    document.addEventListener('visibilitychange', refresh)
    window.addEventListener('online', refresh)
    const timer = !window.CleverTapNative && notificationsConfigured() ? window.setInterval(refresh, 15000) : null
    return () => {
      document.removeEventListener('visibilitychange', refresh)
      window.removeEventListener('online', refresh)
      if (timer !== null) window.clearInterval(timer)
    }
  }, [refreshUnreadCount])

  const openNotification = async (messageId: string) => {
    const identity = currentEmployee.current
    const destination = await requestNotifications<LeadDestination | null>('openMessage', { messageId })
    if (identity !== currentEmployee.current) return
    if (destination?.leadId && destination.recipientEmployeeId === identity) navigate(getLeadNotificationPath(destination))
    void refreshUnreadCount()
  }

  const messages = inbox.employeeId === employeeId ? inbox.messages : []
  const unreadCount = messages.filter((message) => !message.isRead).length
  useEffect(() => { syncOsBadge(unreadCount) }, [unreadCount])

  return <NotificationContext.Provider value={{ messages, unreadCount, totalCount: messages.length,
    available: window.CleverTapNative ? inbox.configured : notificationsConfigured(),
    loading: loading || (!!employeeId && inbox.configured && !inbox.ready),
    error, refreshUnreadCount, openNotification }}>
    {!window.CleverTapNative && notificationsConfigured() && <>
      <span id='clevertap-web-inbox-anchor' hidden aria-hidden='true' />
      <style>{'ct-web-inbox, #unviewedBadge { display: none !important; }'}</style>
    </>}
    {children}</NotificationContext.Provider>
}

export default NotificationProvider
